import React, {
  useMemo,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from 'react';
import {
  TablePaginatedV8,
  CellLeftV8,
  order2Sorting,
  TableCellV8,
  FormCol3,
  EditCellV8,
  CellCenterV8,
  CellYesNoV8,
  CellYesNoEngV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateTimeV8,
  CellDateV8,
  EyeColumnV8,
  toastFailure,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  dateColumn,
  dateExpiryColumn,
} from 'shared-pages/common/grid-helpfunc';
import {
  collectorsWhere,
  FieldMultiMaterialDlg,
  useGetCollectorAsOptionsQuery,
} from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { Company } from 'shared-pages/common/constants';
import { useGetRecLazyQuery } from 'shared-pages/components/field-select/fs-edoc-series-type';
import { useHandleError } from 'shared-pages/services';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const { handleGraphqlError } = useHandleError('series');
  const [updateEdocSerie] = useMutation(gqlUpdate);
  const [addNewEdocSerie] = useMutation(gqlAdd);
  const [deleteEdocSerie] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('SeriesForm'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'docPrefix',
        header: t('Πρόθεμα Σειράς'),
        columnName: t('Πρόθεμα Σειράς'),
        accessorKey: 'docPrefix',
        orderByField: 'docPrefix',
        cell: TableCellV8,
        size: 10,
        meta: { type: 'text' },
      },
      {
        id: 'name',
        header: t('Περιγραφή Σειράς'),
        columnName: t('Περιγραφή Σειράς'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'seriestypeId',
        header: t('Τύπος Σειράς'),
        columnName: t('Τύπος Σειράς'),
        accessorKey: 'seriestypeId',
        orderByField: 'edocSeriesType.name',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'select',
          hook: useGetRecLazyQuery,

          idKey: 'seriestypeId',
          idName: 'edocSeriesType.name',
        },
      },
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'collectorId',
              header: t('Συλλέκτης'),
              columnName: t('Συλλέκτης'),
              accessorKey: 'collectorId',
              orderByField: 'collector.name',
              cell: TableCellV8,
              size: 50,
              meta: {
                type: 'select',
                hook: useGetCollectorAsOptionsQuery,
                where: collectorsWhere,
                idKey: 'collectorId',
                idName: 'collector.name',
              },
            },
          ]
        : []),
      {
        id: 'startNumber',
        header: t('Αριθμός Έναρξης'),
        columnName: t('Αριθμός Έναρξης'),
        accessorKey: 'startNumber',
        orderByField: 'startNumber',
        cell: TableCellV8,
        size: 10,
        meta: { type: 'text' },
      },
      {
        id: 'endNumber',
        header: t('Αριθμός Λήξης'),
        columnName: t('Αριθμός Λήξης'),
        accessorKey: 'endNumber',
        orderByField: 'endNumber',
        cell: TableCellV8,
        size: 10,
        meta: { type: 'text' },
      },
      {
        id: 'leadingZeros',
        header: t('Μηδενικά'),
        columnName: t('Μηδενικά'),
        accessorKey: 'leadingZeros',
        orderByField: 'leadingZeros',
        cell: TableCellV8,
        size: 8,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
      },
      {
        id: 'edocSeriesNumbers.id',
        header: t('Τελευταίος Κωδικός'),
        columnName: t('Τελευταίος Κωδικός'),
        accessorKey: 'edocSeriesNumbers.id',
        orderByField: 'edocSeriesNumbers.id',
        cell: ({ row }) => {
          let edocSeriesNumbers;
          if (
            Array.isArray(row.original?.edocSeriesNumbers) &&
            row?.original?.edocSeriesNumbers?.length > 0
          ) {
            edocSeriesNumbers = row?.original?.edocSeriesNumbers[0];
          }
          return <div>{edocSeriesNumbers?.lastCode ?? ''}</div>;
        },
        size: 10,
        meta: { type: 'text' },
      },
      isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                let edocSeriesNumbers;
                if (
                  Array.isArray(row?.original?.edocSeriesNumbers) &&
                  row?.original?.edocSeriesNumbers?.length > 0
                ) {
                  edocSeriesNumbers = row?.original?.edocSeriesNumbers[0];
                }
                return !edocSeriesNumbers?.lastCode ? EditCellV8({ row, table }) : <></>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="drivers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
              leadingZeros: false,
              collectorId: appStore.isCollector ? appStore.associatedId : null,
            };
          }}
          onBeforeSaveRow={async (state, row, changes) => {
            console.log('$$$ onBeforeSaveRow', state, row, changes);
            if (row?.isNotActive !== undefined && row.isNotActive === true) return true;
            // Validate
            if (!row.collectorId && !changes.collectorId) {
              toastFailure(t('Πρέπει να δηλώσετε Συλλέκτη'));
              return false;
            }
            return true;
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row?.id;
            console.log('$$$ Series - OnSaveRow row', row, changes);
            const values =
              tableStore.state === TableDataState.insert ? { ...row } : { ...row, ...changes }; // Το changes δεν έχει το collectorId
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

            //parseInt startNumber endNumber
            mutValues.startNumber = parseInt(mutValues.startNumber);
            mutValues.endNumber = parseInt(mutValues.endNumber);
            console.log('$$$ Series - OnSaveRow mutValues', mutValues);
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewEdocSerie({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                  handleGraphqlError('mutation-update-failed', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addEdocSeries.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateEdocSerie({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                  handleGraphqlError('mutation-update-failed', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Οδηγό <strong>${row?.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteEdocSerie({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Οδηγός');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
